import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  Card,
  Collapse,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Select,
  Empty,
} from "antd";
import { ColumnConfigModal, ConfigField } from "../../common/ColumnConfigModal";
import WallsSectionBuildUp from "./WallsSectionBuildUp";
import {
  wallSpecifications,
  WallSpecification,
  wallCategories,
  wallFieldToSpecId,
  BuildUpItem,
  resolveWallsBuildUpItemCode,
  isFacingBrickworkItem,
  isBlockworkBuildUpItem,
  getWallsBuildUpRateMultiplier,
} from "../../../mock/walls-section-build-up";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { useTender } from "../../../contexts/TenderContext";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import {
  useGetRetainingWallsBuildUp,
  RETAINING_WALLS_BUILD_UP_QUERY_KEYS,
} from "../../../hooks/useRetainingWallsBuildUp";
import { retainingWallsBuildUpApi } from "../../../api/retainingWallsBuildUp";
import { saveRetainingWallsBuildUp } from "../../../utils/wallsBuildUpSave";
import { useQueryClient } from "@tanstack/react-query";
import { getNumericBoqVersion } from "../../../utils/boqVersionPrefix";

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface WallsSpecificationContainerProps {
  selectedWallType?: string;
  onSelectedWallTypeChange?: (value: string) => void;
  configModalVisible?: boolean;
  setConfigModalVisible?: (open: boolean) => void;
  tempFacingBrickworkLabel?: string;
  setTempFacingBrickworkLabel?: (value: string) => void;
  onWallTypeOptionsChange?: (options: { value: string; label: string }[]) => void;
}

const WallsSpecificationContainer: React.FC<WallsSpecificationContainerProps> = ({
  selectedWallType: selectedWallTypeProp,
  configModalVisible: configModalVisibleProp,
  setConfigModalVisible: setConfigModalVisibleProp,
  tempFacingBrickworkLabel: tempFacingBrickworkLabelProp,
  setTempFacingBrickworkLabel: setTempFacingBrickworkLabelProp,
  onWallTypeOptionsChange,
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [selectedWallTypeLocal] = useState<string>("all");
  const [configModalVisibleLocal, setConfigModalVisibleLocal] = useState(false);
  const [tempFacingBrickworkLabelLocal, setTempFacingBrickworkLabelLocal] = useState<string>('Facing Brickwork – free issue');

  const selectedWallType = selectedWallTypeProp ?? selectedWallTypeLocal;
  const configModalVisible = configModalVisibleProp ?? configModalVisibleLocal;
  const setConfigModalVisible = setConfigModalVisibleProp ?? setConfigModalVisibleLocal;
  const tempFacingBrickworkLabel = tempFacingBrickworkLabelProp ?? tempFacingBrickworkLabelLocal;
  const setTempFacingBrickworkLabel = setTempFacingBrickworkLabelProp ?? setTempFacingBrickworkLabelLocal;
  const { settings } = useGlobalSettings();
  const { state: tender, updateField, saveTender } = useTender();
  const queryClient = useQueryClient();
  const [boqVersionNumber, setBoqVersionNumber] = useState<number>(() => {
    const raw = localStorage.getItem("currentBoqVersion");
    const v = raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 1) : 1; return v || 1;
  });

  // Re-read boqVersionNumber when localStorage changes (e.g. after save from another component)
  useEffect(() => {
    const handleStorage = () => {
      const raw = localStorage.getItem("currentBoqVersion");
      const v = raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 1) : 1;
      setBoqVersionNumber(v || 1);
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("versionDataReloaded", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("versionDataReloaded", handleStorage);
    };
  }, []);

  const { data: persistedBuildUp, isFetched: persistedBuildUpFetched, isFetching: persistedBuildUpFetching } = useGetRetainingWallsBuildUp(
    tender.projectName,
    boqVersionNumber,
    { enabled: !!tender.projectName, staleTime: 0 }
  );

  // Set of spec IDs the API has authoritative data for. The seeding/fallback
  // paths must never overwrite or substitute mock items for these specs — the
  // API is the source of truth whenever it has a record.
  // IMPORTANT: only count a spec as "API-owned" when it actually has items.
  // A spec that comes back with `items: []` (e.g. a previous save before the
  // user filled in a quantity, or a stale empty payload) would otherwise
  // block the mock-template seeding effect below and the user would see an
  // empty Build Up panel for a newly-added wall type. Filtering to
  // `items.length > 0` keeps the API authoritative for real persisted
  // build-ups while still letting the seeding effect populate brand-new
  // specs from the mock template.
  const apiSpecIds = useMemo(() => {
    const ids = new Set<string>();
    persistedBuildUp?.specifications?.forEach((s) => {
      if (s.items && s.items.length > 0) ids.add(s.id);
    });
    return ids;
  }, [persistedBuildUp]);

  // True once the API query has resolved at least once for this tender+version.
  // Until this flips true we must NOT seed mock data — otherwise the mock items
  // race the in-flight API response and can be persisted back to the server by
  // the debounced save before hydration overwrites them.
  const apiSettled = persistedBuildUpFetched && !persistedBuildUpFetching;

  const hasHydrated = useRef(false);

  // Reset hydration flag when version changes so fresh data from a new version is applied
  useEffect(() => {
    hasHydrated.current = false;
  }, [boqVersionNumber, tender.projectName]);

  // Fetch item library rates for this region (same pattern as GroundworksReview)
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");


  // Helper: match evaluateCode against resourceCode from API array and return nettRate
  const getRateFromLibrary = useCallback(
    (evaluateCode: string): number => {
      const items = itemLibraryData as unknown as any[];
      if (!Array.isArray(items) || items.length === 0) return 0;
      const match = items.find(
        (lib) =>
          lib.resourceCode?.trim().toUpperCase() ===
          evaluateCode.trim().toUpperCase(),
      );
      return match?.nettRate ?? 0;
    },
    [itemLibraryData],
  );

  // Facing brickwork label from config (falls back to free-issue label when unset)
  const facingBrickworkLabel = tender?.groundworks?.wallsBuildUpConfig?.facingBrickworkLabel ?? 'Facing Brickwork – free issue';

  // Get saved walls build up data from context
  const savedWallsBuildUpData = tender?.groundworks?.wallsBuildUpData || {};

  // Imported items from "Brick&con wall section build up" sheet
  const importedDimensionsRaw: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> =
    (tender?.groundworks as any)?.wallsBuildUpDimensions || {};

  // Build description (lowercase) → mock item id map across all specs for rate code lookup
  const descriptionToMockId = useMemo(() => {
    const map = new Map<string, string>();
    wallSpecifications.forEach((spec) =>
      spec.items.forEach((item) => map.set(item.description.toLowerCase(), item.id))
    );
    return map;
  }, []);

  const importedDimensionsBySpec = useMemo(() => {
    const bySpec: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> = {};
    Object.entries(importedDimensionsRaw).forEach(([specId, items]) => {
      bySpec[specId] = items;
    });
    return bySpec;
  }, [importedDimensionsRaw]);

  // Refs to track latest values for proper closure handling. These MUST be
  // updated synchronously during render (not in a useEffect) so that rapid
  // edit handlers — which run before React commits the next render — read
  // the latest values. The earlier useEffect-based update lagged by one
  // render, which caused handleSpecificationChange to spread a stale
  // `groundworksRef.current` and silently revert other groundworks fields
  // (visible as "data pulls old; refreshes correctly from API").
  const wallsBuildUpDataRef = useRef(savedWallsBuildUpData);
  const groundworksRef = useRef(tender?.groundworks);
  const tenderRef = useRef(tender);
  const itemLibraryRef = useRef(itemLibraryData);
  const ohpRef = useRef(settings.groundworks.overheadAndProfit);
  const prelimsRef = useRef(settings.groundworks.preliminaries);
  const versionRef = useRef(boqVersionNumber);
  wallsBuildUpDataRef.current = savedWallsBuildUpData;
  groundworksRef.current = tender?.groundworks;
  tenderRef.current = tender;
  itemLibraryRef.current = itemLibraryData;
  ohpRef.current = settings.groundworks.overheadAndProfit;
  prelimsRef.current = settings.groundworks.preliminaries;
  versionRef.current = boqVersionNumber;

  // Hydrate tender context once on mount from persisted data
  useEffect(() => {
    // `persistedBuildUp` can resolve to `{}` (no `specifications`) rather than
    // `undefined` when the API has nothing saved yet for this tender/version —
    // still truthy, so the guard above alone doesn't catch it; `?.forEach`
    // below (matching the same-shaped `persistedBuildUp?.specifications?.forEach`
    // a few lines up) no-ops instead of throwing on that empty-object case.
    if (!persistedBuildUp || hasHydrated.current) return;
    hasHydrated.current = true;

    const buildUpData: Record<string, Array<{ id: string; rate: number; labour: number; total: number }>> = {};
    const dimensionData: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> = {};

    persistedBuildUp.specifications?.forEach((spec) => {
      buildUpData[spec.id] = spec.items.map((item) => ({
        id: item.id,
        rate: item.rate,
        labour: item.labour,
        total: item.total,
      }));
      dimensionData[spec.id] = spec.items.map((item) => ({
        description: item.description,
        length: item.length ?? 0,
        width: item.width ?? 0,
        height: item.height ?? 0,
        net: item.net,
        unit: item.unit,
      }));
    });

    updateField("groundworks", {
      ...groundworksRef.current,
      wallsBuildUpData: buildUpData,
      wallsBuildUpDimensions: dimensionData,
      ...(persistedBuildUp.config ? { wallsBuildUpConfig: persistedBuildUp.config } : {}),
    });
  }, [persistedBuildUp, updateField]);

  // Debounced save timeout ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Handle specification change - save to context and trigger save
  const handleSpecificationChange = useCallback(
    (specId: string, updatedItems: BuildUpItem[]) => {
      const itemsData = updatedItems.map((item) => ({
        id: item.id,
        rate: item.rate,
        labour: item.labour,
        total: item.total,
      }));

      // Persist length/width/height/net back to dimensions so the BoQ Summary,
      // Excel Export, and Plot Matrix (which all read wallsBuildUpDimensions)
      // see the user's edits.
      const dimensionsData = updatedItems.map((item) => ({
        description: item.description,
        length: item.length ?? 0,
        width: item.width ?? 0,
        height: item.height ?? 0,
        net: item.net,
        unit: item.unit,
      }));

      const updatedWallsBuildUpData = {
        ...wallsBuildUpDataRef.current,
        [specId]: itemsData,
      };

      const existingDimensions =
        ((groundworksRef.current as any)?.wallsBuildUpDimensions || {}) as
        Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>>;

      const updatedDimensions = {
        ...existingDimensions,
        [specId]: dimensionsData,
      };

      // Update ref immediately for subsequent calls
      wallsBuildUpDataRef.current = updatedWallsBuildUpData;

      updateField("groundworks", {
        ...groundworksRef.current,
        wallsBuildUpData: updatedWallsBuildUpData,
        wallsBuildUpDimensions: updatedDimensions,
      });

      // Debounced save - save after 1 second of no changes. Also pushes the
      // latest build-up to the dedicated walls API so the React Query cache
      // can refetch and any subsequent hydration reads the authoritative
      // server state (previously this only happened on Save & Continue, so
      // mid-session the API would lag the local edits and any re-hydration
      // would clobber them with stale data — "data pulls old; refresh fixes
      // it" was the symptom).
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await saveTender();
          await saveRetainingWallsBuildUp({
            tender: tenderRef.current,
            itemLibraryData: itemLibraryRef.current,
            ohpPercent: ohpRef.current,
            prelimsPercent: prelimsRef.current,
            versionOverride: versionRef.current,
          });
          // Allow the next persistedBuildUp arrival to re-hydrate the context
          // (it would otherwise be blocked by `hasHydrated.current === true`).
          hasHydrated.current = false;
          await queryClient.invalidateQueries({
            queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(
              tenderRef.current.projectName,
              versionRef.current,
            ),
          });
        } catch (err) {
          console.error("Failed to persist retaining walls build-up:", err);
        }
      }, 1000);
    },
    [updateField, saveTender, queryClient],
  );

  // Calculate totals for each wall type from retainingWallsTable
  const wallTypeTotals = useMemo(() => {
    const retainingWallsData = tender?.groundworks?.retainingWallsTable || [];
    const totals: { [key: string]: number } = {};

    // Initialize all wall types to 0
    Object.keys(wallFieldToSpecId).forEach((fieldName) => {
      totals[fieldName] = 0;
    });

    // Sum up values for each wall type
    retainingWallsData.forEach((row: any) => {
      Object.keys(wallFieldToSpecId).forEach((fieldName) => {
        const value = row[fieldName];
        if (typeof value === "number" && !isNaN(value)) {
          totals[fieldName] += value;
        }
      });
    });

    return totals;
  }, [tender?.groundworks?.retainingWallsTable]);

  // Get wall types that have values > 0
  const activeWallTypeIds = useMemo(() => {
    return Object.entries(wallTypeTotals)
      .filter(([_, total]) => total > 0)
      .map(([fieldName, _]) => wallFieldToSpecId[fieldName]);
  }, [wallTypeTotals]);

  // Seed blank build-up templates from the mock ONLY for newly-added wall
  // specs that have a quantity in the take-off but no record on the API yet.
  // This must wait until the API query has resolved — otherwise the mock
  // races the in-flight response and can be persisted back to the server.
  // API-known specs are skipped entirely; the hydration effect above is the
  // only path that writes their dimensions into context.
  useEffect(() => {
    if (activeWallTypeIds.length === 0) return;
    if (!apiSettled) return;

    const existing =
      ((groundworksRef.current as any)?.wallsBuildUpDimensions || {}) as
      Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>>;

    const seeded = { ...existing };
    let mutated = false;

    activeWallTypeIds.forEach((specId) => {
      if (apiSpecIds.has(specId)) return;
      if (existing[specId] && existing[specId].length > 0) return;
      const spec = wallSpecifications.find((s) => s.id === specId);
      if (!spec) return;
      // Seed the activity list (descriptions + units) from the mock template
      // but zero out every numeric field. The user fills in L / W / H as
      // they take off; Net auto-computes from L × W × H; rate comes from
      // the item library via `filteredSpecifications`. Without zeroing,
      // any pre-filled L / W / H / Net on the mock items (e.g.
      // `facing_bricks_1_wide` has length=1, width=1, height=0.75) would
      // show up as if the user had already entered them.
      seeded[specId] = spec.items.map((item) => ({
        description: item.description,
        length: 0,
        width: 0,
        height: 0,
        net: 0,
        unit: item.unit,
      }));
      mutated = true;
    });

    if (mutated) {
      updateField("groundworks", {
        ...groundworksRef.current,
        wallsBuildUpDimensions: seeded,
      });
    }
  }, [activeWallTypeIds, apiSettled, apiSpecIds, updateField]);

  // Filter specifications by selected wall type and only those with values > 0
  const filteredSpecifications = useMemo(() => {
    let filtered = wallSpecifications.filter((spec) =>
      activeWallTypeIds.includes(spec.id),
    );

    if (selectedWallType !== "all") {
      filtered = filtered.filter((spec) => spec.type === selectedWallType);
    }

    return filtered.map((spec) => {
      const savedData = savedWallsBuildUpData[spec.id];
      const apiItems = persistedBuildUp?.specifications?.find((s) => s.id === spec.id)?.items;
      const importedItems = importedDimensionsBySpec[spec.id];

      let mergedItems: BuildUpItem[];

      if (apiItems && apiItems.length > 0) {
        // Source of truth: the persisted build-up from the API. Read it
        // DIRECTLY (not via wallsBuildUpDimensions in tender state) so we
        // never display mock values left over in tender state from a prior
        // visit while waiting for the hydration effect to commit.
        //
        // BUT — when the user types in Length / Width / Height / Net, the
        // edit lands in `state.groundworks.wallsBuildUpDimensions` via
        // `handleSpecificationChange` and won't reach the API (and the
        // React Query cache) until the debounced 1 s save fires. Reading
        // dimensions only from `apiItems` would freeze the displayed
        // value at the previous library snapshot until the save completes,
        // producing the "becomes 0, then changes after a while" symptom.
        // Overlay the in-context dimensions on top of the API row so the
        // edited number renders synchronously on every keystroke.
        const localDimByDesc = new Map<string, { length?: number; width?: number; height?: number; net: number }>();
        importedItems?.forEach((d) => {
          const key = String(d.description ?? "").trim().toLowerCase();
          if (key) localDimByDesc.set(key, d);
        });

        mergedItems = apiItems.filter((item) => {
          const desc = String(item.description ?? "").trim();
          return desc.length > 0 && !/^[0-9.]+$/.test(desc);
        }).map((item) => {
          const descKey = (item.description || "").toLowerCase();
          const mockId = descriptionToMockId.get(descKey);
          const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
          const evaluateCode = resolveWallsBuildUpItemCode(id, facingBrickworkLabel);
          const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
          const savedItem = savedData?.find((s: any) => s.id === id);
          const apiRate = (item as any).rate;
          // Facing Brickwork items must always reflect the current
          // Configure-dropdown choice. Both `savedItem.rate` and `apiRate`
          // are snapshots taken when the previous variant was active (the
          // hydration effect copies the API spec straight into
          // `wallsBuildUpData`, so `savedItem.rate` is non-zero even when
          // the user has never manually edited it). Bypassing both caches
          // and using `libraryRate` directly is the only way the rate
          // refreshes when the dropdown flips between GWALPGW0020 and
          // GWALPGW0021. Manual overrides are still possible via the
          // editable Rate cell — they re-enter via `handleSpecificationChange`
          // and persist on the same save cycle.
          const isFacingBrick = isFacingBrickworkItem(id);
          // Facing-brickwork half-brick widths (×1 for 0.5 br, ×2 for 1 br, …,
          // ×7 for 3.5 br) and 100mm-blockwork course counts (×1–×8) share
          // the `libraryRate × multiplier` pattern — always recompute, ignoring
          // stale savedItem / apiRate snapshots. See `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
          const isMultiplied = isBlockworkBuildUpItem(id);
          const rateMultiplier = getWallsBuildUpRateMultiplier(id);
          // Aquaseal memb was remapped from GWALPGW0028 → GWALPGW0029; bypass
          // stale saved/api snapshots so the new library rate flows through.
          const forceLibraryRate = id === 'aquaseal_memb';
          const rate = isMultiplied
            ? libraryRate * rateMultiplier
            : forceLibraryRate
              ? libraryRate
              : savedItem && savedItem.rate !== 0
                ? savedItem.rate
                : (apiRate && apiRate !== 0 ? apiRate : libraryRate);
          void isFacingBrick; // kept for the dropdown-driven `evaluateCode` resolution above

          // Per-keystroke overlay (see comment above). `?? item.X` keeps a
          // user-cleared `0` (legitimate edit) instead of falling back to
          // the cached API value.
          const localDim = localDimByDesc.get(descKey.trim());
          const length = localDim ? localDim.length : item.length;
          const width = localDim ? localDim.width : item.width;
          const height = localDim ? localDim.height : item.height;
          const net = localDim ? localDim.net : item.net;

          return {
            id,
            description: item.description,
            length: length || undefined,
            width: width || undefined,
            height: height || undefined,
            net,
            unit: item.unit,
            rate,
            total: net * rate,
          } as BuildUpItem;
        });
      } else if (!apiSettled) {
        // API still loading — render nothing rather than leak mock values
        // (or stale mock-derived values from tender state) onto the screen.
        // The panel will populate on the next render once the API responds.
        mergedItems = [];
      } else if (importedItems && importedItems.length > 0) {
        // API has responded with no record for this spec, but tender state
        // has imported dimensions (Excel takeoff import path). Use those.
        mergedItems = importedItems.filter((imported) => {
          const desc = String(imported.description ?? "").trim();
          return desc.length > 0 && !/^[0-9.]+$/.test(desc);
        }).map((imported) => {
          const descKey = imported.description.toLowerCase();
          const mockId = descriptionToMockId.get(descKey);
          const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
          const evaluateCode = resolveWallsBuildUpItemCode(id, facingBrickworkLabel);
          const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
          const savedItem = savedData?.find((s: any) => s.id === id);
          // Facing-brickwork widths and 100mm-blockwork course counts always
          // re-derive `libraryRate × multiplier` so the row refreshes when
          // the Configure dropdown changes between GWALPGW0020 and
          // GWALPGW0021 (facing brick) and so stale persisted snapshots
          // can't lock the rate — see `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
          const isMultiplied = isBlockworkBuildUpItem(id);
          const rateMultiplier = getWallsBuildUpRateMultiplier(id);
          const forceLibraryRate = id === 'aquaseal_memb';
          const rate = isMultiplied
            ? libraryRate * rateMultiplier
            : forceLibraryRate
              ? libraryRate
              : savedItem?.rate ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate) : libraryRate;
          return {
            id,
            description: imported.description,
            length: imported.length || undefined,
            width: imported.width || undefined,
            height: imported.height || undefined,
            net: imported.net,
            unit: imported.unit,
            rate,
            total: imported.net * rate,
          };
        });
      } else {
        // API has responded with no record AND no imported dimensions —
        // this is a newly-added wall type with quantity in the take-off.
        // Mock template is the correct fallback here (the seeding effect
        // will commit these items into context on the next render).
        mergedItems = spec.items.map((mockItem) => {
          const evaluateCode = resolveWallsBuildUpItemCode(mockItem.id, facingBrickworkLabel);
          const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
          const savedItem = savedData?.find((s: any) => s.id === mockItem.id);
          // Facing-brickwork widths and 100mm-blockwork course counts always
          // re-derive `libraryRate × multiplier` — see `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
          const isMultiplied = isBlockworkBuildUpItem(mockItem.id);
          const rateMultiplier = getWallsBuildUpRateMultiplier(mockItem.id);
          const forceLibraryRate = mockItem.id === 'aquaseal_memb';
          const rate = isMultiplied
            ? libraryRate * rateMultiplier
            : forceLibraryRate
              ? libraryRate
              : savedItem?.rate
                ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate)
                : libraryRate;
          return {
            ...mockItem,
            rate,
            total: mockItem.net * rate,
          } as BuildUpItem;
        });
      }

      const netTotal = mergedItems.reduce((sum, item) => sum + Math.abs(item.total), 0);
      const ohpPercent = settings.groundworks.overheadAndProfit;
      const prelimsPercent = settings.groundworks.preliminaries;
      const markupFactor = (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);
      const ohpAmount = netTotal * (ohpPercent / 100);
      const grandTotal = netTotal * markupFactor;

      return { ...spec, items: mergedItems, netTotal, ohpPercent, prelimsPercent, ohpAmount, grandTotal };
    });
  }, [
    selectedWallType,
    settings.groundworks.overheadAndProfit,
    settings.groundworks.preliminaries,
    activeWallTypeIds,
    savedWallsBuildUpData,
    getRateFromLibrary,
    importedDimensionsBySpec,
    descriptionToMockId,
    apiSettled,
    apiSpecIds,
    persistedBuildUp,
    facingBrickworkLabel,
  ]);

  const getWallTypeColor = (wallId: string): string => {
    const colors: { [key: string]: string } = {
      // Garden Retaining Walls - Purple/Blue shades
      grw450: "#722ed1",
      grw600: "#1890ff",
      grw750: "#531dab",
      grw900: "#2f54eb",
      grw1050: "#13c2c2",
      grw1200: "#096dd9",
      grw1500: "#1d39c4",
      grw1800: "#391085",
      grw2000: "#0050b3",
      // Drive Retaining Walls - Green/Yellow shades
      drw450: "#52c41a",
      drw600: "#389e0d",
      drw750: "#237804",
      drw900: "#faad14",
      drw1050: "#d48806",
      drw1200: "#ad6800",
      drw1500: "#876800",
      drw1800: "#5b8c00",
      drw2000: "#3f6600",
      drw2250: "#254000",
      drw2500: "#fa8c16",
      drw2750: "#d46b08",
      drw3000: "#ad4e00",
      // Concrete Core Walls - Purple shades
      ccrw1500: "#9254de",
      ccrw1800: "#7c3aed",
      ccrw2000: "#6d28d9",
      ccrw2250: "#5b21b6",
      ccrw2500: "#4c1d95",
      ccrw2750: "#7e22ce",
      ccrw3000: "#6b21a8",
      ccrw3250: "#581c87",
      ccrw3500: "#46178c",
    };
    return colors[wallId] || "#666666";
  };

  // Get unique categories that have active specifications
  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    filteredSpecifications.forEach((spec) => {
      categories.add(spec.type);
    });
    return Array.from(categories);
  }, [filteredSpecifications]);

  // Wall type options for dropdown - only show categories that have values
  const wallTypeOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Wall Types" }];

    // Check which categories have active walls
    const hasGarden = activeWallTypeIds.some((id) => id.startsWith("grw"));
    const hasConcrete = activeWallTypeIds.some((id) => id.startsWith("ccrw"));
    const hasDrive = activeWallTypeIds.some((id) => id.startsWith("drw"));

    if (hasGarden) {
      options.push({ value: "garden", label: "Garden Retaining Walls" });
    }
    if (hasConcrete) {
      options.push({
        value: "concrete",
        label: "Concrete Core Retaining Walls",
      });
    }
    if (hasDrive) {
      options.push({ value: "drive", label: "Drive Retaining Walls" });
    }

    return options;
  }, [activeWallTypeIds]);

  // Push computed options up to parent so the lifted Wall Type select can render them.
  useEffect(() => {
    onWallTypeOptionsChange?.(wallTypeOptions);
  }, [wallTypeOptions, onWallTypeOptionsChange]);

  // When parent changes the selected wall type, collapse any open panels.
  useEffect(() => {
    setActiveKeys([]);
  }, [selectedWallType]);

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <div className="mb-4">
          <Title level={4} className="mb-2">
            Walls Section Build Up Specifications
          </Title>
          <Text type="secondary" className="block">
            Detailed construction specifications and cost breakdowns for
            different wall types.
          </Text>
        </div>
      </div>

      <Row gutter={[16, 16]} className="mb-6">
        <Col span={6}>
          <Card size="small" className="text-center">
            <Text type="secondary" className="block mb-1">
              Available Specifications
            </Text>
            <Title level={4} className="mb-0 text-blue-600">
              {filteredSpecifications.length}
            </Title>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" className="text-center">
            <Text type="secondary" className="block mb-1">
              Total Items
            </Text>
            <Title level={4} className="mb-0 text-green-600">
              {filteredSpecifications.reduce(
                (sum, spec) => sum + spec.items.length,
                0,
              )}
            </Title>
          </Card>
        </Col>

        <Col span={6}>
          <Card size="small" className="text-center">
            <Text type="secondary" className="block mb-1">
              Category
            </Text>
            <Title level={4} className="mb-0 text-purple-600">
              {selectedWallType === "all"
                ? "All Types"
                : wallCategories[
                    selectedWallType as keyof typeof wallCategories
                  ]}
            </Title>
          </Card>
        </Col>
      </Row>

      {/* Empty state when no wall types have values */}
      {activeWallTypeIds.length === 0 && (
        <Empty
          description={
            <span>
              No wall types have values in the Ret & Screen Take off table.
              <br />
              Please add values in the "Ret & Screen Take off" tab first.
            </span>
          }
          className="py-12"
        />
      )}

      {activeWallTypeIds.length > 0 && filteredSpecifications.length > 0 && (
        <Collapse
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(keys as string[])}
          className="walls-specifications-collapse"
          size="large"
        >
          {filteredSpecifications.map((specification: WallSpecification) => (
            <Panel
              key={specification.id}
              header={
                <div className="flex justify-between items-center w-full">
                  <Space size="large" align="center">
                    <Tag
                      color={getWallTypeColor(specification.id)}
                      className="font-bold text-sm px-3 py-1"
                      style={{ margin: 0 }}
                    >
                      {specification.name}
                    </Tag>
                    <div className="flex flex-col">
                      <Text strong className="text-base">
                        {specification.category}{" "}
                        {specification.name.replace(/[A-Z]{2,4}W?/, "")}mm
                      </Text>
                      <Text type="secondary" className="text-xs">
                        {specification.items.length} construction items
                      </Text>
                    </div>
                  </Space>
                  <div className="flex items-center gap-6">
                    <div className="flex flex-col items-end">
                      <Text type="secondary" className="text-xs">
                        Takeoff Total
                      </Text>
                      <Text strong className="text-base text-blue-600">
                        {(wallTypeTotals[specification.id] || 0).toFixed(3)} m
                      </Text>
                    </div>
                    <div className="flex flex-col items-end">
                      <Text strong className="text-lg text-green-600">
                        £{Math.abs(specification.grandTotal).toFixed(2)}
                      </Text>
                      {((specification.prelimsPercent ?? 0) > 0 || specification.ohpPercent > 0) && (
                        <Text type="secondary" className="text-xs">
                          Inc.{specification.ohpPercent > 0 && ` ${specification.ohpPercent}% OHP`}{specification.ohpPercent > 0 && (specification.prelimsPercent ?? 0) > 0 && ' +'}{(specification.prelimsPercent ?? 0) > 0 && ` ${specification.prelimsPercent}% Prelims`}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              }
              className={`wall-specification-panel wall-${specification.id}`}
              extra={null}
            >
              <WallsSectionBuildUp
                specification={specification}
                onSpecificationChange={(updatedSpec) =>
                  handleSpecificationChange(specification.id, updatedSpec.items)
                }
                facingBrickworkLabel={facingBrickworkLabel}
              />
            </Panel>
          ))}
        </Collapse>
      )}

      {/* Show message when filter returns no results but there are active walls */}
      {activeWallTypeIds.length > 0 &&
        filteredSpecifications.length === 0 &&
        selectedWallType !== "all" && (
          <Empty
            description={
              <span>
                No{" "}
                {
                  wallCategories[
                    selectedWallType as keyof typeof wallCategories
                  ]
                }{" "}
                have values.
                <br />
                Try selecting "All Wall Types" to see available specifications.
              </span>
            }
            className="py-12"
          />
        )}

      {/* Configure Build Up Headers Modal */}
      <ColumnConfigModal
        title="Configure Walls Build Up Headers"
        open={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        onApply={async () => {
          const newConfig = {
            ...tender.groundworks?.wallsBuildUpConfig,
            facingBrickworkLabel: tempFacingBrickworkLabel,
          };

          // Apply the new Facing Brickwork rate to every facing_bricks_*
          // row so the change propagates through every downstream consumer
          // that reads `wallsBuildUpData[specId][].rate` — Bill Summary on
          // Groundworks Review, Sum Analysis, BoQ JSON payload, Plot Matrix,
          // Excel export, and the next /retaining-walls-buildup POST. The
          // resolver picks GWALPGW0021 (Facing Brickwork) or GWALPGW0020
          // (Facing Brickwork - M&J Supply) per spec.
          const newFacingCode = resolveWallsBuildUpItemCode('facing_bricks_1_wide', tempFacingBrickworkLabel);
          const newFacingRate = newFacingCode ? getRateFromLibrary(newFacingCode) : 0;

          const existingBuildUpData = (tender.groundworks?.wallsBuildUpData ?? {}) as Record<string, Array<{ id: string; rate: number; labour?: number; total: number }>>;
          const existingDimensions = ((tender.groundworks as any)?.wallsBuildUpDimensions ?? {}) as Record<string, Array<{ id?: string; description: string; net: number }>>;

          const updatedBuildUpData: typeof existingBuildUpData = {};
          Object.entries(existingBuildUpData).forEach(([specId, items]) => {
            updatedBuildUpData[specId] = items.map((item) => {
              if (!isFacingBrickworkItem(item.id)) return item;
              // Look up the matching dimension row so total = net × rate
              // stays in sync with the dimension's net.
              const dimItem = existingDimensions[specId]?.find((d) => d.id === item.id);
              const net = dimItem?.net ?? 0;
              // Apply the half-brick-width multiplier (×1 for 0.5 br, ×2 for
              // 1 br, …, ×7 for 3.5 br) — matches the `libraryRate × N`
              // pattern Bill Summary, Sum Analysis, BoQ Excel and the BoQ
              // JSON payload all use.
              const rateMultiplier = getWallsBuildUpRateMultiplier(item.id);
              const rate = newFacingRate * rateMultiplier;
              return { ...item, rate, total: net * rate };
            });
          });

          updateField('groundworks', {
            ...tender.groundworks,
            wallsBuildUpConfig: newConfig,
            wallsBuildUpData: updatedBuildUpData,
          });
          setConfigModalVisible(false);

          // Persist the config + re-priced specs to /retaining-walls-buildup
          // immediately so the change is durable without waiting for a
          // Save & Continue action. Re-prices every facing_bricks_* item on
          // the existing specs with the new library rate so downstream
          // consumers reading from the API match the in-memory state.
          try {
            const existingSpecs = persistedBuildUp?.specifications ?? [];
            const repricedSpecs = existingSpecs.map((spec) => ({
              ...spec,
              items: spec.items.map((item) => {
                if (!isFacingBrickworkItem(item.id)) return item;
                const net = item.net ?? 0;
                // Same `newFacingRate × N` half-brick-width multiplier as
                // the in-memory update above.
                const rateMultiplier = getWallsBuildUpRateMultiplier(item.id);
                const rate = newFacingRate * rateMultiplier;
                return { ...item, rate, total: net * rate };
              }),
            }));
            await retainingWallsBuildUpApi.post({
              tenderId: tender.id,
              tenderName: tender.projectName,
              tenderVersion: boqVersionNumber,
              specifications: repricedSpecs,
              config: newConfig,
            });
            // Force consumers (Bill Summary, Sum Analysis, etc.) to see the
            // freshly-priced specs on next mount instead of the cached pre-Apply payload.
            await queryClient.invalidateQueries({
              queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(tender.projectName, boqVersionNumber),
            });
            // The hydration effect is gated on hasHydrated.current — reset so
            // the refreshed persistedBuildUp commits back into context.
            hasHydrated.current = false;
          } catch (err) {
            console.error('Failed to persist walls build-up config:', err);
          }
        }}
        width={420}
        isViewMode={tender.isViewMode}
      >
        <ConfigField label="Facing Brickwork">
          <Select
            value={tempFacingBrickworkLabel}
            onChange={(val: string) => setTempFacingBrickworkLabel(val)}
            style={{ width: '100%' }}
            options={[
              { value: 'Facing Brickwork – free issue', label: 'Facing Brickwork – free issue' },
              { value: 'Facing Brickwork - M&J Supply', label: 'Facing Brickwork - M&J Supply' },
            ]}
          />
        </ConfigField>
      </ColumnConfigModal>
    </div>
  );
};

export default WallsSpecificationContainer;
